import { Container, type ContainerOptions } from "../core/Container.js";
import type { WheelEventArgs } from "../core/events.js";
import { clamp, type Point, type Rect, type Size } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { ScrollBar } from "./ScrollBar.js";

/**
 * `hidden` is WPF's: the axis still scrolls by wheel, pan and `scrollIntoView`, but no bar is
 * drawn and none takes room. `never` is WPF's `Disabled` - the axis does not scroll at all.
 */
export type ScrollBarVisibility = "auto" | "always" | "hidden" | "never";

export interface ScrollableOptions extends ContainerOptions {
  horizontalScroll?: ScrollBarVisibility;
  verticalScroll?: ScrollBarVisibility;
}

/**
 * Shared scrolling machinery for ListBox, ListView and ScrollViewer.
 *
 * Owns the two scroll bars, decides which of them are needed (accounting for
 * the fact that showing one can force the other), and exposes the resulting
 * viewport. Subclasses supply `contentSize` and paint inside `viewportRect`.
 */
export abstract class ScrollableBase extends Container {
  readonly hScrollBar: ScrollBar;
  readonly vScrollBar: ScrollBar;

  horizontalScroll: ScrollBarVisibility;
  verticalScroll: ScrollBarVisibility;

  #hVisible = false;
  #vVisible = false;

  constructor(options: ScrollableOptions = {}) {
    super({ width: 240, height: 160, ...options });
    this.horizontalScroll = options.horizontalScroll ?? "auto";
    this.verticalScroll = options.verticalScroll ?? "auto";

    this.hScrollBar = new ScrollBar({ orientation: "horizontal", visible: false });
    this.vScrollBar = new ScrollBar({ orientation: "vertical", visible: false });
    this.hScrollBar.on("scroll", () => this.onScrollChanged());
    this.vScrollBar.on("scroll", () => this.onScrollChanged());
    this.controls.add(this.hScrollBar);
    this.controls.add(this.vScrollBar);

    this.on("wheel", (e) => this.onWheel(e));
  }

  /** Total extent of the scrollable content, in content units. */
  protected abstract contentSize(theme: Theme): Size;

  /** Border inset between the control's edge and the scrollable area. */
  protected contentInset(theme: Theme): number {
    return theme.metrics.controlCornerRadius > 0 ? 1 : 2;
  }

  /**
   * Height reserved at the top of the control for non-scrolling chrome —
   * ListView's column header. It sits above the viewport but beside the
   * vertical scroll bar, matching Win32 list views.
   */
  protected viewportInsetTop(_theme: Theme): number {
    return 0;
  }

  /**
   * Content scroll offsets. Deliberately *not* named scrollX/scrollY: those
   * are Container fields that shift child controls, and the scroll bars are
   * children that must stay put.
   */
  get scrollOffsetX(): number {
    return this.hScrollBar.value;
  }
  set scrollOffsetX(v: number) {
    this.hScrollBar.value = v;
  }

  get scrollOffsetY(): number {
    return this.vScrollBar.value;
  }
  set scrollOffsetY(v: number) {
    this.vScrollBar.value = v;
  }

  get horizontalVisible(): boolean {
    return this.#hVisible;
  }

  get verticalVisible(): boolean {
    return this.#vVisible;
  }

  /** Area the scrollable content is drawn into, in local coordinates. */
  /** Scrolled content is clipped tight: it must never escape the viewport. */
  protected override get clipSlack(): number {
    return 0;
  }

  get viewportRect(): Rect {
    const theme = this.desktop?.theme;
    if (!theme) return this.localRect;
    const inset = this.contentInset(theme);
    const bar = theme.metrics.scrollBarSize;
    const top = this.viewportInsetTop(theme);
    return {
      x: inset,
      y: inset + top,
      width: Math.max(0, this.width - inset * 2 - (this.#vVisible ? bar : 0)),
      height: Math.max(0, this.height - inset * 2 - top - (this.#hVisible ? bar : 0)),
    };
  }

  /**
   * Recompute scroll bar visibility, placement and ranges. Called on resize
   * and whenever the subclass's content changes.
   */
  updateScrollBars(): void {
    const theme = this.desktop?.theme;
    if (!theme) return;
    const inset = this.contentInset(theme);
    const bar = theme.metrics.scrollBarSize;
    const content = this.contentSize(theme);
    const top = this.viewportInsetTop(theme);
    const availW = Math.max(0, this.width - inset * 2);
    const availH = Math.max(0, this.height - inset * 2);
    const scrollableH = Math.max(0, availH - top);

    // Two passes: adding one bar shrinks the other axis and may require it too.
    let showH = this.horizontalScroll === "always";
    let showV = this.verticalScroll === "always";
    for (let pass = 0; pass < 2; pass++) {
      if (this.horizontalScroll === "auto") {
        showH = content.width > availW - (showV ? bar : 0) + 0.5;
      }
      if (this.verticalScroll === "auto") {
        showV = content.height > scrollableH - (showH ? bar : 0) + 0.5;
      }
    }
    if (this.horizontalScroll === "never" || this.horizontalScroll === "hidden") showH = false;
    if (this.verticalScroll === "never" || this.verticalScroll === "hidden") showV = false;

    this.#hVisible = showH;
    this.#vVisible = showV;
    this.hScrollBar.visible = showH;
    this.vScrollBar.visible = showV;

    const viewW = availW - (showV ? bar : 0);
    // Full height the vertical bar occupies, and the shorter height the
    // scrollable content actually gets once the header is taken out.
    const barH = availH - (showH ? bar : 0);
    const viewH = Math.max(0, barH - top);

    // Ranged whether or not it is on screen. A bar's range describes the
    // content, not its own visibility, and one left with the range it had
    // while it was needed answers `maxValue` for text that is no longer there
    // — so anything that scrolls to the end, as a chat log following new
    // messages does, pushes the content clean out of the viewport with no bar
    // left to bring it back.
    this.hScrollBar.setRange(0, content.width, viewW);
    this.vScrollBar.setRange(0, content.height, viewH);
    // Bounds are the part that only a visible bar has any use for.
    if (showH) this.hScrollBar.setBounds(inset, inset + barH, Math.max(0, viewW), bar);
    if (showV) this.vScrollBar.setBounds(inset + viewW, inset, bar, Math.max(0, barH));
  }

  protected override performLayout(): void {
    this.updateScrollBars();
  }

  protected override onEnvironmentChanged(): void {
    this.updateScrollBars();
    this.clampScroll();
  }

  /** Subclasses re-render (and may re-virtualize) when the offsets change. */
  protected onScrollChanged(): void {
    this.emit("scroll");
    this.invalidate();
  }

  protected onWheel(e: WheelEventArgs): void {
    if (e.ctrlKey) return; // reserved for zoom in ScrollViewer
    let handled = false;
    // Shift+wheel scrolls horizontally, as it does everywhere on Windows.
    const wantHorizontal = e.shiftKey || (e.deltaX !== 0 && e.deltaY === 0);
    if (wantHorizontal && this.#scrollsH(this.#hVisible) && !this.hScrollBar.isFull) {
      const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      const before = this.hScrollBar.value;
      this.hScrollBar.scrollBy(delta);
      handled = this.hScrollBar.value !== before;
    } else if (this.#scrollsV(this.#vVisible) && !this.vScrollBar.isFull) {
      const before = this.vScrollBar.value;
      this.vScrollBar.scrollBy(e.deltaY);
      handled = this.vScrollBar.value !== before;
    }
    if (handled) e.handled = true;
  }

  /** Scroll the smallest amount that brings `r` (content coords) into view. */
  scrollIntoView(r: Rect): void {
    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;
    const x = this.hScrollBar.value;
    const y = this.vScrollBar.value;

    if (r.x < x) this.hScrollBar.value = r.x;
    else if (r.x + r.width > x + view.width) this.hScrollBar.value = r.x + r.width - view.width;

    if (r.y < y) this.vScrollBar.value = r.y;
    else if (r.y + r.height > y + view.height) this.vScrollBar.value = r.y + r.height - view.height;
  }

  /** Bottom-right gap where the two bars meet; themes fill it flat. */
  protected paintScrollCorner(rc: RenderContext, theme: Theme): void {
    if (!this.#hVisible || !this.#vVisible) return;
    const inset = this.contentInset(theme);
    const bar = theme.metrics.scrollBarSize;
    rc.fillRect(
      {
        x: this.width - inset - bar,
        y: this.height - inset - bar,
        width: bar,
        height: bar,
      },
      theme.palette.scrollTrack.a > 0 ? theme.palette.scrollTrack : theme.palette.windowBg,
    );
  }

  /**
   * Two-finger panning, and anything else that scrolls both axes at once.
   *
   * Unlike the wheel, which picks an axis, a pan is genuinely diagonal — so
   * both bars move and both report what they took. A bar that is already at
   * its end consumes nothing, which is what lets the leftover reach an outer
   * scroller instead of the gesture dying against the inner one.
   */
  override panBy(dx: number, dy: number): Point {
    // Keyed off the bars themselves rather than the private visibility flags,
    // so an axis the application turned off never pans and the rule stays
    // testable without a desktop to compute a layout against.
    let used = { x: 0, y: 0 };
    if (dx !== 0 && this.#scrollsH(this.hScrollBar.visible) && !this.hScrollBar.isFull) {
      const before = this.hScrollBar.value;
      this.hScrollBar.scrollBy(dx);
      used = { ...used, x: this.hScrollBar.value - before };
    }
    if (dy !== 0 && this.#scrollsV(this.vScrollBar.visible) && !this.vScrollBar.isFull) {
      const before = this.vScrollBar.value;
      this.vScrollBar.scrollBy(dy);
      used = { ...used, y: this.vScrollBar.value - before };
    }
    return used;
  }

  #scrollsH(barShown: boolean): boolean {
    return barShown || this.horizontalScroll === "hidden";
  }

  #scrollsV(barShown: boolean): boolean {
    return barShown || this.verticalScroll === "hidden";
  }

  /** Clamp both offsets after the content shrinks. */
  protected clampScroll(): void {
    this.hScrollBar.value = clamp(this.hScrollBar.value, 0, this.hScrollBar.maxValue);
    this.vScrollBar.value = clamp(this.vScrollBar.value, 0, this.vScrollBar.maxValue);
  }
}
